import * as Yup from 'yup';

const urlRegex =
  /^(https?:\/\/)?(www\.)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;

const optionalUrl = () =>
  Yup.string()
    .transform((value, originalValue) => {
      const v = (originalValue ?? '').trim();
      return v === '' ? null : v; // ✅ empty -> null
    })
    .nullable()
    // If your Yup version supports this option, it's perfect:
    .matches(urlRegex, { message: 'Invalid URL', excludeEmptyString: true });

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const todayISO = () => new Date().toLocaleDateString('en-CA');
// Local calendar date (not UTC) so "today" comparisons don't drift by one
// day for users east/west of Greenwich around midnight.
const dateToISO = (d) =>
  d instanceof Date && !isNaN(d) ? d.toLocaleDateString('en-CA') : null;

const fileValidation = Yup.mixed()
  .nullable()
  .test('fileSize', 'The file is too large', (value) => {
    if (!value) return true; // no file, don't fail
    return value.size <= MAX_FILE_SIZE;
  });

const fileArrayValidation = Yup.array()
  .of(fileValidation)
  .max(10, 'You can only upload up to 10 pictures')
  .nullable(true);

export const brandSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
      'Password must contain at least 8 characters, one uppercase letter, one lowercase letter and one number'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  country: Yup.string().required('Country is required'),
  city: Yup.string().required('City is required'),
  description: Yup.string()
    .trim()
    .max(200, 'Short description must be at most 200 characters'),
  longDescription: Yup.string()
    .trim()
    .max(10000, 'Long description must be at most 10,000 characters'),
  foundingYear: Yup.number().positive().integer(),
  categories: Yup.array()
    .of(
      Yup.object().shape({
        macroCategory: Yup.number().required(),
        microCategory: Yup.array().of(Yup.number()).required(),
      })
    )
    .min(1, 'At least one category is required'),
  website: optionalUrl(),
  facebook: optionalUrl(),
  instagram: optionalUrl(),
  tiktok: optionalUrl(),

  socialsOthers: Yup.array()
    .of(
      Yup.object({
        label: Yup.string().trim().min(2, 'Too short').max(50, 'Too long'),
        url: optionalUrl(),
      })
    )
    .nullable(true),

  avatar: Yup.mixed().test(
    'fileSize',
    'The file is too large',
    (value) => !value || (value && value.size <= MAX_FILE_SIZE)
  ),
  media: Yup.array()
    .of(
      Yup.mixed().test(
        'fileSize',
        'One or more files are too large',
        (value) => !value || (value && value.size <= MAX_FILE_SIZE)
      )
    )
    .max(10, 'You can only upload up to 10 media files'),
});

export const promoterSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .matches(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
      'Password must contain at least 8 characters, one uppercase letter, one lowercase letter and one number'
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  registeredDate: Yup.date(),
  lastLoginDate: Yup.date(),
  profileInfo: Yup.string(),
  notificationSettings: Yup.object().shape({
    // Define the shape if you have known properties or leave it as an empty object
  }),
  resetToken: Yup.string(),
  avatar: Yup.mixed().test('fileSize', 'The file is too large', (value) => {
    // Allow the field to be optional (skip validation if no file is uploaded)
    if (!value || value.length === 0) return true;
    // Check file size (assuming limit is 2MB)
    return value[0].size <= 2000000; // 2MB
  }),
  companyName: Yup.string(),
  country: Yup.string().required('Country is required'),
  city: Yup.string().required('City is required'),
  fiscalNumber: Yup.string()
    .required('Fiscal number is required')
    .matches(/^\d+$/, 'Fiscal number must contain only digits'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .matches(/^\+?[0-9\s().-]{6,20}$/, 'Invalid phone number'),
  firm: Yup.string(),
  iban: Yup.string()
    .nullable()
    .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/, 'Invalid IBAN format'),
  documentProof: Yup.mixed()
    .nullable()
    .test('fileSize', 'Document must be ≤ 5MB', (value) => {
      if (!value || value.length === 0) return true;
      return value[0].size <= 5 * 1024 * 1024;
    })
    .test('fileType', 'Supported formats: PDF, JPG, PNG', (value) => {
      if (!value || value.length === 0) return true;
      return ['application/pdf', 'image/jpeg', 'image/png'].includes(
        value[0].type
      );
    }),
  headquarters: Yup.string(),
  // The form field is named websiteUrl (a `website` rule here would never run)
  websiteUrl: optionalUrl(),
  // T2B-423: connecting Easypay is required, so signup will not go through
  // until the promoter has said which it is — they've done it, or they're
  // deferring and accept that their account is on hold. Neither answer unlocks
  // event creation; that waits on there2b receiving their Easypay account id.
  easypayOnboardingChoice: Yup.string()
    .oneOf(['declared', 'deferred'], 'Choose one of the two Easypay options')
    .required('Choose one of the two Easypay options'),
});

export const eventSchema = [
  // Step 1: Event Details
  Yup.object().shape({
    name: Yup.string().required('Event name is required'),
    location: Yup.string().required('Location is required'),
    locationPlaceId: Yup.string().required('Location is required'),

    locationCoords: Yup.object()
      .shape({
        lat: Yup.number()
          .typeError('Latitude is required')
          .required('Latitude is required'),
        lng: Yup.number()
          .typeError('Longitude is required')
          .required('Longitude is required'),
      })
      .required('Coordinates are required'),

    date: Yup.object()
      .shape({
        type: Yup.string()
          .oneOf(['fixed', 'segmented'], 'Invalid event type')
          .required('Event type is required'),

        startDate: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .nullable()
          .when('type', {
            is: 'fixed',
            then: (schema) =>
              schema
                .required('Event start date is required')
                .test('not-past', 'Start date cannot be in the past', (value) => {
                  if (!value) return true;
                  return dateToISO(value) >= todayISO();
                }),
            otherwise: (schema) => schema.notRequired(),
          }),

        endDate: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? null : value
          )
          .nullable()
          .when('type', {
            is: 'fixed',
            then: (schema) =>
              schema
                .required('Event end date is required')
                .test('not-past', 'End date cannot be in the past', (value) => {
                  if (!value) return true;
                  return dateToISO(value) >= todayISO();
                })
                .test(
                  'after-start',
                  'End must be after start',
                  function (value) {
                    if (!value) return true;
                    const { startDate, openHours, closeHours } = this.parent;
                    if (!startDate) return true;
                    const startDT = `${dateToISO(startDate)} ${openHours || '00:00'}`;
                    const endDT = `${dateToISO(value)} ${closeHours || '23:59'}`;
                    return endDT > startDT;
                  }
                ),
            otherwise: (schema) => schema.notRequired(),
          }),

        openHours: Yup.string().when('type', {
          is: 'fixed',
          then: (schema) => schema.required('Opening hours are required'),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),

        closeHours: Yup.string().when('type', {
          is: 'fixed',
          then: (schema) =>
            schema
              .required('Closing hours are required')
              .test(
                'after-open',
                'Closing hours must be after opening hours',
                function (value) {
                  if (!value) return true;
                  const { startDate, endDate, openHours } = this.parent;
                  if (!openHours) return true;
                  const sameDay =
                    startDate &&
                    endDate &&
                    dateToISO(startDate) === dateToISO(endDate);
                  if (!sameDay) return true;
                  return value > openHours;
                }
              ),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),

        days: Yup.array()
          .of(
            Yup.object().shape({
              date: Yup.date()
                .transform((value, originalValue) =>
                  originalValue === '' ? null : value
                )
                .required('Date is required')
                .test('not-past', 'Date cannot be in the past', (value) => {
                  if (!value) return true;
                  return dateToISO(value) >= todayISO();
                }),
              openHours: Yup.string().required('Opening hours are required'),
              closeHours: Yup.string()
                .required('Closing hours are required')
                .test(
                  'after-open',
                  'Closing hours must be after opening hours',
                  function (value) {
                    if (!value) return true;
                    const { openHours } = this.parent;
                    if (!openHours) return true;
                    return value > openHours;
                  }
                ),
            })
          )
          .when('type', {
            is: 'segmented',
            then: (schema) =>
              schema
                .min(1, 'At least one date must be added')
                .required()
                .test(
                  'no-duplicate-days',
                  'Duplicate days are not allowed',
                  (days) => {
                    if (!Array.isArray(days)) return true;
                    const seen = new Set();
                    for (const d of days) {
                      const iso = dateToISO(d?.date);
                      if (!iso) continue;
                      if (seen.has(iso)) return false;
                      seen.add(iso);
                    }
                    return true;
                  }
                )
                .test(
                  'chronological-order',
                  'Days must be in chronological order',
                  (days) => {
                    if (!Array.isArray(days)) return true;
                    let prev = null;
                    for (const d of days) {
                      const iso = dateToISO(d?.date);
                      if (!iso) continue;
                      if (prev && iso <= prev) return false;
                      prev = iso;
                    }
                    return true;
                  }
                ),
            otherwise: (schema) => schema.notRequired(),
          }),
      })
      .required(),

    categoriesAllowed: Yup.array()
      .of(
        Yup.object().shape({
          macroCategoryId: Yup.number().required(),
          microCategoryIds: Yup.array().of(Yup.number()).nullable(true),
        })
      )
      .required('At least one category is required'),

    longDescription: Yup.string().required('Long description is required'),
    shortDescription: Yup.string().required('Short description is required'),

    segment: Yup.object()
      .shape({
        segmentDate: Yup.date(),
        segmentOpeningHours: Yup.string(),
        segmentClosingHours: Yup.string(),
      })
      .nullable(true),
  }),

  // Step 2: Media & Amenities
  Yup.object().shape({
    eventIcon: fileValidation,
    landscapeImage: fileValidation,
    morePictures: fileArrayValidation,
    regulatoryDocument: fileValidation,
    utilities: Yup.array().of(Yup.string()),
  }),

  // Step 3: Space Creation
  Yup.object().shape({
    spaces: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required('Name is required'),
          size: Yup.number().positive().integer().required('Size is required'),
          description: Yup.string()
            .trim()
            .max(255, 'Description must be 255 characters or less')
            .required('Description is required'),
          quantity: Yup.number()
            .positive()
            .integer()
            .required('Availability is required'),
          price: Yup.number()
            .positive()
            .max(99999999.99, 'Price is too large')
            // Stored as DECIMAL(10,2) — reject extra decimals instead of
            // silently rounding them away on the server.
            .test(
              'max-2-decimals',
              'Price can have at most 2 decimal places',
              (v) =>
                v == null || Math.abs(v * 100 - Math.round(v * 100)) < 1e-6
            )
            .required('Price is required'),
        })
      )
      .min(1, 'At least one space is required')
      .max(100, 'A maximum of 100 space types is allowed')
      // Placements fall back to matching spaces by name, so duplicates
      // would make floorplan pins ambiguous.
      .test('unique-names', 'Space names must be unique', (spaces) => {
        if (!Array.isArray(spaces)) return true;
        const names = spaces
          .map((s) => (s?.name || '').trim().toLowerCase())
          .filter(Boolean);
        return new Set(names).size === names.length;
      })
      .required(),
  }),

  // Step 4: Floor Planning — validates the fields the form actually uses
  // (floorplanPreview / spacePlacements). The preview can be a blob: URL,
  // so no .url() check. spaceId is a client-side uuid at creation time and
  // is deliberately not validated; the API links placements to Space rows.
  Yup.object().shape({
    createFloorplan: Yup.boolean(),
    floorplanPreview: Yup.string()
      .nullable()
      .when('createFloorplan', {
        is: true,
        then: (schema) => schema.required('Floorplan image is required'),
        otherwise: (schema) => schema.nullable(),
      }),

    spacePlacements: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          spaceName: Yup.string().required('Space name is required'),
          xPercent: Yup.number()
            .min(0)
            .max(1)
            .required('X position is required'),
          yPercent: Yup.number()
            .min(0)
            .max(1)
            .required('Y position is required'),
        })
      )
      .nullable(),
  }),

  // Step 5: Summary (No validations)
  Yup.object().shape({}),
];
